// apiUtils.js - API utility functions

export const API_BASE = process.env.REACT_APP_API_BASE || '/api/v1';

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };

  return fetch(url, defaultOptions);
};

/**
 * Turns a failed apiRequest() response into the message that should actually reach the user.
 * Added 2026-09-23 — many service functions across the app used to do
 * `if (!r.ok) throw new Error('Failed to X')`, discarding whatever real message the backend
 * sent (e.g. Batch close's exact reconciliation variance, "150 kg unaccounted for..."). That
 * made hard validation errors indistinguishable from a generic network hiccup, and gave the
 * user nothing to act on. Also special-cases 401 — an expired session was previously
 * surfaced (if at all) as a raw "Failed to update status", not something that told the user
 * what actually went wrong or what to do about it.
 *
 * Usage: `if (!r.ok) throw new Error(await extractErrorMessage(r, 'Failed to update status'));`
 */
export const extractErrorMessage = async (response, fallback) => {
  if (response.status === 401) {
    return 'Your session has expired — please log in again.';
  }
  try {
    const data = await response.json();
    return data?.message || data?.error || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Send chat message
 * @param {string} message - The message text
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object>} - API response
 */
export const sendChatMessage = async (message, sessionId, organizationId, department) => {
  const messageData = {
    message: message,
    sessionId: sessionId,
    organizationId: organizationId,
    department: department
  };
  
  console.log('Sending chat message:', messageData);
  console.log('Organization ID being sent:', organizationId);
  console.log('Department being sent:', department);
  
  const response = await apiRequest('/chat/message', {
    method: 'POST',
    body: JSON.stringify(messageData)
  });

  console.log('Chat message response status:', response.status);
  console.log('Chat message response ok:', response.ok);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Chat message error response:', errorText);
    throw new Error(`Failed to send message: ${response.status} ${errorText}`);
  }

  const responseData = await response.json();
  console.log('Chat message response data:', responseData);
  return responseData;
};

/**
 * Fetch chat conversations
 * @returns {Promise<Array>} - Conversations list (empty array if error)
 */
export const fetchConversations = async () => {
  try {
    const response = await apiRequest('/chat/conversations');
    
    if (response.ok) {
      const conversations = await response.json();
      return Array.isArray(conversations) ? conversations : [];
    } else {
      console.warn('Failed to fetch conversations:', response.status);
      return [];
    }
  } catch (error) {
    console.warn('Error fetching conversations:', error.message);
    return [];
  }
};

/**
 * Create new chat conversation
 * @param {number} organizationId - Organization ID
 * @returns {Promise<Object>} - New conversation data
 */
export const createNewConversation = async (organizationId) => {
  console.log('Creating new conversation for organization:', organizationId);
  
  const response = await apiRequest(`/chat/conversations?organizationId=${organizationId}`, {
    method: 'POST'
  });

  console.log('Create conversation response status:', response.status);
  console.log('Create conversation response ok:', response.ok);

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Create conversation error response:', errorText);
    throw new Error(`Failed to create conversation: ${response.status} ${errorText}`);
  }

  const responseData = await response.json();
  console.log('Create conversation response data:', responseData);
  return responseData;
};

/**
 * Fetch chat history for session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Chat history (empty array if error)
 */
export const fetchChatHistory = async (sessionId) => {
  try {
    const response = await apiRequest(`/chat/history/${sessionId}`);
    
    if (response.ok) {
      const history = await response.json();
      return Array.isArray(history) ? history : [];
    } else {
      console.warn('Failed to fetch chat history:', response.status);
      return [];
    }
  } catch (error) {
    console.warn('Error fetching chat history:', error.message);
    return [];
  }
};

/**
 * Rename conversation
 * @param {string} sessionId - Session ID
 * @param {string} title - New title
 * @returns {Promise<void>}
 */
export const renameConversation = async (sessionId, title) => {
  const response = await apiRequest(`/chat/conversations/${encodeURIComponent(sessionId)}/title`, {
    method: 'PUT',
    body: JSON.stringify({ title })
  });

  if (!response.ok) {
    throw new Error('Failed to rename conversation');
  }
};

/**
 * Delete conversation
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export const deleteConversation = async (sessionId) => {
  const response = await apiRequest(`/chat/conversations/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE'
  });

  if (!response.ok && response.status !== 204 && response.status !== 404) {
    throw new Error('Failed to delete conversation');
  }
};

/**
 * Fetch question suggestions
 * @param {string} sessionId - Session ID
 * @param {number} maxSuggestions - Maximum suggestions
 * @returns {Promise<Array>} - Suggestions array (empty array if error)
 */
export const fetchQuestionSuggestions = async (sessionId, maxSuggestions = 5) => {
  try {
    const response = await apiRequest(`/chat/suggestions?sessionId=${encodeURIComponent(sessionId)}&maxSuggestions=${maxSuggestions}`);
    
    if (response.ok) {
      const suggestions = await response.json();
      return Array.isArray(suggestions) ? suggestions : [];
    } else {
      // Gracefully handle non-ok responses (like 404, 500, etc.)
      console.warn('Failed to fetch suggestions:', response.status);
      return [];
    }
  } catch (error) {
    // Gracefully handle network errors, parsing errors, etc.
    console.warn('Error fetching suggestions:', error.message);
    return [];
  }
};

/**
 * Fetch departments for organization
 * @param {number} organizationId - Organization ID
 * @returns {Promise<Array>} - Departments array
 */
export const fetchDepartments = async (organizationId) => {
  const response = await apiRequest(`/documents/departments?organizationId=${organizationId}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch departments');
  }

  return response.json();
};

/**
 * Create new department
 * @param {Object} departmentData - Department data
 * @returns {Promise<Object>} - Created department
 */
export const createDepartment = async (departmentData) => {
  const response = await apiRequest('/admin/departments/create', {
    method: 'POST',
    body: JSON.stringify(departmentData)
  });

  if (!response.ok) {
    throw new Error('Failed to create department');
  }

  return response.json();
};

/**
 * Create new user
 * @param {Object} userData - User data
 * @returns {Promise<Object>} - Created user
 */
export const createUser = async (userData) => {
  const response = await apiRequest('/admin/users/create', {
    method: 'POST',
    body: JSON.stringify(userData)
  });

  if (!response.ok) {
    throw new Error('Failed to create user');
  }

  return response.json();
};

/**
 * Fetch user form data
 * @returns {Promise<Object>} - Form data (organizations, departments, groups)
 */
export const fetchUserFormData = async () => {
  const response = await apiRequest('/admin/users/form-data');
  
  if (!response.ok) {
    throw new Error('Failed to fetch form data');
  }

  return response.json();
};

/**
 * Create new organization
 * @param {Object} orgData - Organization data
 * @returns {Promise<Object>} - Created organization
 */
export const createOrganization = async (orgData) => {
  const response = await apiRequest('/admin/organizations/create-org', {
    method: 'POST',
    body: JSON.stringify(orgData)
  });

  if (!response.ok) {
    throw new Error('Failed to create organization');
  }

  return response.json();
};

/**
 * Upload document
 * @param {FormData} formData - Form data with file and metadata
 * @returns {Promise<Object>} - Upload result
 */
export const uploadDocument = async (formData) => {
  const response = await apiRequest('/documents/upload', {
    method: 'POST',
    body: formData,
    headers: {} // Don't set Content-Type for FormData
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Upload failed');
  }

  return response.json();
};

/**
 * Fetch documents list
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} - Documents list
 */
export const fetchDocuments = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await apiRequest(`/documents/list?${queryString}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch documents');
  }

  return response.json();
};

/**
 * Delete document
 * @param {number} documentId - Document ID
 * @returns {Promise<void>}
 */
export const deleteDocument = async (documentId) => {
  const response = await apiRequest(`/documents/delete/${documentId}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Delete failed');
  }
};

/**
 * Reprocess document
 * @param {number} documentId - Document ID
 * @returns {Promise<void>}
 */
export const reprocessDocument = async (documentId) => {
  const response = await apiRequest(`/documents/${documentId}/reprocess`, {
    method: 'POST'
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Reprocess failed');
  }
};

/**
 * Generate business report
 * @param {Object} reportData - Report data
 * @returns {Promise<Blob>} - PDF blob
 */
export const generateBusinessReport = async (reportData) => {
  // Session cookie (credentials: 'include', set by apiRequest) already authenticates this call —
  // the backend disables httpBasic entirely (SecurityConfig.java), so this endpoint was never
  // actually protected by the header below. Removed 2026-08-20: it served no function except
  // shipping a real admin password in plaintext inside the public JS bundle.
  const response = await apiRequest('/reports/business/generate', {
    method: 'POST',
    body: JSON.stringify(reportData),
  });

  if (!response.ok) {
    throw new Error('Failed to generate report');
  }

  // Check if response is a PDF file
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/pdf")) {
    return response.blob();
  } else {
    return response.json();
  }
};
